import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, ArrowRight, ChevronDown, Loader2, Plus, Wand2, X } from '@digital-twins/design-system/icons'
import {
  COUNTRIES,
  SOCIAL_BRAND_ROLES,
  SOCIAL_BRAND_SEARCHES_MAX,
  SOCIAL_BRIEF_MAX_LENGTH,
  SOCIAL_PLATFORM_SOURCE_KINDS,
  SOCIAL_MAX_BRANDS,
  SOCIAL_MAX_KEYWORDS,
  SOCIAL_MAX_LANGUAGES,
  SOCIAL_MAX_TOPICS,
  SOCIAL_NOTE_MAX_LENGTH,
  SOCIAL_SEARCH_TERM_MAX_LENGTH,
  SOCIAL_SUBJECT_KINDS,
  SOCIAL_TOPIC_NAME_MAX_LENGTH,
} from '@digital-twins/shared-types'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Heading } from '@/components/ui/Heading'
import { Input, inputVariants } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Text } from '@/components/ui/Text'
import { CreditStatusBadge } from '@/components/billing/CreditStatusBadge'
import { useCreditInfo } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import { localizedCountryName } from '@/lib/utils/localized-country-name'
import { SocialPlatformIcon } from './SocialPlatformIcon'
import { SocialNoteDot, SocialTermsInput } from './SocialTermsInput'
import { SocialTrackerLanguageField } from './SocialTrackerLanguageField'
import { SocialTrackerScheduleFields } from './SocialTrackerScheduleFields'
import { SocialUniverseChannelCards } from './SocialUniverseChannelCards'
import { SocialUniverseReview } from './SocialUniverseReview'
import { SocialUniverseStepper } from './SocialUniverseStepper'
import {
  SOCIAL_UNLISTED_MARKET,
  socialMarketFromSelect,
  socialMarketSelectValue,
} from './socialMarket'
import { SocialUsageEstimate } from './SocialUsageEstimate'
import { SocialCreditsCta } from './SocialCreditsCta'
import { socialImportCredits, topicCreditsFromEstimate } from './socialCredits'
import { useRefreshCreditsOnEdge } from './useRefreshCreditsOnEdge'
import { useSocialEstimate } from './useSocialEstimate'
import { SOCIAL_PLATFORM_LABELS } from './socialFormat'
import {
  aliasesFromText,
  brandAliasesText,
  withBrandHashtagValues,
  withBrandSearchValues,
  flushUniverseDrafts,
  keywordsOverLimit,
  SOCIAL_UNIVERSE_PLATFORM_ORDER,
  SOCIAL_UNIVERSE_STEPS,
  sourceCheckLabelKey,
  sourceCheckTone,
  toSocialEstimateRequest,
  universeStepBlockers,
  withBrandNames,
} from './socialUniverseForm'
import { useSocialUniverseWizard } from './useSocialUniverseWizard'

import { ReactNode, SelectHTMLAttributes } from 'react'
import {
  SocialBrandInput,
  SocialBrandRole,
  SocialPlatform,
  SocialSubjectKind,
  SocialTopicInput,
  SocialUniverseItem,
} from '@digital-twins/shared-types'
import {
  UniverseRequiredField,
  UniverseStep,
  UniverseStepBlocker,
} from './socialUniverseForm'
import { SocialUniverseWizard } from './useSocialUniverseWizard'

export interface SocialUniverseDialogProps {
  open: boolean
  /** Null creates a Topic; a Topic edits it. */
  universe: SocialUniverseItem | null
  onOpenChange: (open: boolean) => void
  onSaved: (universe: SocialUniverseItem) => void
}

/** Opening or switching Topic starts a new session; parent rerenders never reset edits. */
export function SocialUniverseDialog(props: SocialUniverseDialogProps) {
  const { open, universe } = props

  if (!open) {
    return null
  }

  return <UniverseDialogSession key={universe?.token ?? 'new'} {...props} />
}

const REQUIRED_MESSAGES = {
  name: 'socialListening.topicDialog.nameRequired',
  keywords: 'socialListening.topicDialog.keywordsRequired',
  channels: 'socialListening.topicDialog.channelsRequired',
  searches: 'socialListening.topicDialog.searchesRequired',
  languages: 'socialListening.topicDialog.languagesRequired',
  market: 'socialListening.topicDialog.marketRequired',
} as const satisfies Record<UniverseRequiredField, string>

type FieldError = (field: UniverseRequiredField) => ReactNode

/**
 * Five steps: Topic, Brands, Channels, Tracking, Review. A new Topic moves on
 * only when the step is complete; an edit opens on Review, can jump to any step
 * and save from each one. On a new Topic's first step Suggest is the main
 * button, with Set up manually beside it; a Suggest that answers, with Topic
 * still complete, lands on Brands with what it found.
 */
function UniverseDialogSession(props: SocialUniverseDialogProps) {
  const { universe, onOpenChange } = props

  const { t, i18n } = useTranslation('app')
  const wizard = useSocialUniverseWizard(props)
  const { form, drafts, missing, isSaving, isEditing } = wizard
  // An edit opens on the overview; every step stays one click away.
  const [step, setStep] = useState<UniverseStep>(universe ? 'review' : 'topic')
  // The furthest step reached: earlier valid steps keep their check when you go back.
  const [furthestIndex, setFurthestIndex] = useState(() =>
    SOCIAL_UNIVERSE_STEPS.indexOf(universe ? 'review' : 'topic'),
  )
  const titleRef = useRef<HTMLHeadingElement>(null)
  const stepHeadingRef = useRef<HTMLHeadingElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const shownStep = useRef<UniverseStep>(step)

  useEffect(() => {
    if (shownStep.current === step) {
      return
    }
    shownStep.current = step
    bodyRef.current?.scrollTo?.({ top: 0 })
    stepHeadingRef.current?.focus()
  }, [step])

  // Where the last Suggest left the client: landed on Brands, or ready there once Topic is complete.
  const [suggestLanding, setSuggestLanding] = useState<'landed' | 'ready' | null>(null)
  const wasSuggesting = useRef(false)
  // A Topic button pressed while it cannot run yet: its reason shows beside the buttons.
  const [blockedPress, setBlockedPress] = useState<'suggest' | 'manual' | null>(null)
  const suggestLoadingKey = useSuggestLoadingKey(wizard.isSuggesting)

  const languageNames = useMemo(() => {
    try {
      return new Intl.DisplayNames([i18n.language], {
        type: 'language',
        fallback: 'code',
      })
    } catch {
      return null
    }
  }, [i18n.language])

  const languageLabel = (code: string) => {
    try {
      return languageNames?.of(code) ?? code
    } catch {
      return code
    }
  }

  // Typed but not yet entered chips count, so Next never waits on an Enter.
  const effectiveForm = flushUniverseDrafts(form, drafts)
  const blockersOf = (value: UniverseStep) =>
    universeStepBlockers(effectiveForm, value)
  const stepIndex = SOCIAL_UNIVERSE_STEPS.indexOf(step)
  const currentBlockers = blockersOf(step)
  const isLastStep = step === 'review'

  const isReachable = (value: UniverseStep) =>
    isEditing ||
    SOCIAL_UNIVERSE_STEPS.slice(0, SOCIAL_UNIVERSE_STEPS.indexOf(value)).every(
      (earlier) => blockersOf(earlier).length === 0,
    )

  const isComplete = (value: UniverseStep) =>
    SOCIAL_UNIVERSE_STEPS.indexOf(value) < Math.max(stepIndex, furthestIndex) &&
    blockersOf(value).length === 0

  const blockerMessage = (blocker: UniverseStepBlocker) =>
    blocker === 'keywordLimit'
      ? t('socialListening.topicDialog.keywordLimit', {
          max: SOCIAL_MAX_KEYWORDS,
          count: keywordsOverLimit(effectiveForm),
        })
      : t(REQUIRED_MESSAGES[blocker])

  const hasEstimate = step === 'tracking' || step === 'review'

  const { data: creditInfo } = useCreditInfo()
  const isOrgOwner = creditInfo?.isOrgOwner === true
  const refreshCredits = useRefreshCreditsOnEdge()
  const creditEstimate = useSocialEstimate({
    request: toSocialEstimateRequest(effectiveForm, universe?.token ?? null),
    shouldLoad: hasEstimate,
  })
  const serverEstimate = creditEstimate.estimate
  // Posts and credits both come from the server estimate (one run plan per channel).
  const credits = useMemo(
    () =>
      topicCreditsFromEstimate({
        estimate: serverEstimate,
        cadence: form.cadence,
        recentRuns: universe ? wizard.runs : [],
        platforms: form.platforms,
        runPostLimit: form.runPostLimit,
        savedRunPostLimit: universe ? universe.runPostLimit : null,
      }),
    [serverEstimate, form.cadence, form.platforms, form.runPostLimit, universe, wizard.runs],
  )
  // Whole credits the next run's imports can hold: a new channel's first import and the imports an edit adds.
  const firstCollectionCredits = useMemo(
    () => (serverEstimate ? socialImportCredits(serverEstimate) : null),
    [serverEstimate],
  )

  // The balance is read once at login: refresh it before the funds check (E1).
  useEffect(() => {
    if (step === 'review') {
      refreshCredits()
    }
  }, [step, refreshCredits])

  // Create only (an edit is never blocked): the server's funds check for one update.
  const isCreateBlocked =
    !isEditing &&
    isLastStep &&
    serverEstimate?.fundsState === 'insufficient'

  const payerName =
    isEditing &&
    universe?.payer?.kind === 'seat' &&
    !universe.payer.isViewer &&
    universe.payer.displayName
      ? universe.payer.displayName
      : null

  // A new Topic opens on Topic with Suggest as its main action and Set up manually beside it.
  const isTopicStart = step === 'topic' && !isEditing

  const stepReason =
    !isEditing && !isLastStep && currentBlockers.length
      ? blockerMessage(currentBlockers[0])
      : isCreateBlocked
        ? t('socialListening.credits.confirm.createBlocked')
        : null

  // Suggest reads the goal, the market and the languages: step 1 complete, plus a goal.
  const topicBlockers = blockersOf('topic')
  const suggestReason = topicBlockers.length
    ? blockerMessage(topicBlockers[0])
    : form.goal.trim() === ''
      ? t('socialListening.topicDialog.suggestNeeds')
      : null

  // On Topic both buttons stay pressable: a press that cannot run says why, and the line
  // follows the form until it is fixed. Later steps keep a disabled Next with its reason.
  const nextReason =
    step === 'topic'
      ? blockedPress === 'suggest'
        ? suggestReason
        : blockedPress === 'manual' && isTopicStart
          ? stepReason
          : null
      : stepReason

  const fieldError: FieldError = (field) =>
    missing.includes(field) ? (
      <Text
        as="span"
        size="xs"
        className="block text-destructive"
        role="alert"
        id={`social-universe-${field}-error`}
        data-testid={`social-universe-${field}-error`}
      >
        {t(REQUIRED_MESSAGES[field])}
      </Text>
    ) : null

  const goTo = (next: UniverseStep) => {
    wizard.flushDrafts()
    setStep(next)
    setFurthestIndex((current) => Math.max(current, SOCIAL_UNIVERSE_STEPS.indexOf(next)))
    setSuggestLanding(null)
    setBlockedPress(null)
  }

  // A Suggest run from Topic that answered: on to Brands when Topic is complete, else say it is ready there.
  // Watched here so the wizard hook stays as it is. A failed Suggest keeps the client on Topic with its error.
  useEffect(() => {
    if (wizard.isSuggesting) {
      wasSuggesting.current = true
      setSuggestLanding(null)
      return
    }
    if (!wasSuggesting.current) {
      return
    }
    wasSuggesting.current = false
    if (wizard.suggestFailed || step !== 'topic') {
      return
    }
    if (blockersOf('topic').length === 0) {
      goTo('brands')
      setSuggestLanding('landed')
      return
    }
    setSuggestLanding('ready')
    // Only the end of a Suggest decides; the form and step are read as they are then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizard.isSuggesting])

  /** Suggest from Topic: it runs when step 1 is complete and has a goal, else says what it needs. */
  const onSuggest = () => {
    if (wizard.isSuggesting || isSaving) {
      return
    }
    if (suggestReason) {
      setBlockedPress('suggest')
      return
    }
    setBlockedPress(null)
    wizard.suggest()
  }

  /** Set up manually: on to Brands without Suggest, once step 1 is complete. */
  const onSetUpManually = () => {
    if (wizard.isSuggesting) {
      return
    }
    if (currentBlockers.length) {
      setBlockedPress('manual')
      return
    }
    goTo('brands')
  }

  const onNext = () => {
    // Enter on a new Topic's first step moves on as Set up manually does: it never runs Suggest.
    if (isTopicStart) {
      onSetUpManually()
      return
    }
    if (isLastStep || (!isEditing && currentBlockers.length)) {
      return
    }
    goTo(SOCIAL_UNIVERSE_STEPS[stepIndex + 1])
  }

  const onBack = () => {
    if (stepIndex > 0) {
      goTo(SOCIAL_UNIVERSE_STEPS[stepIndex - 1])
    }
  }

  const onSave = async () => {
    const blocked = await wizard.save()
    if (blocked) {
      goTo(blocked)
    }
  }

  const stepTitleKey =
    step === 'review' && isEditing
      ? ('socialListening.topicDialog.stepTitles.reviewEdit' as const)
      : (`socialListening.topicDialog.stepTitles.${step}` as const)

  const removalLabel = wizard.pendingRemoval
    ? SOCIAL_PLATFORM_LABELS[wizard.pendingRemoval]
    : ''

  const saveLabel = t(
    universe
      ? isSaving
        ? 'socialListening.topicDialog.saving'
        : 'socialListening.topicDialog.save'
      : isSaving
        ? 'socialListening.topicDialog.creating'
        : 'socialListening.topicDialog.create',
  )

  const funds = isCreateBlocked ? (
    <CreditStatusBadge
      totalCost={serverEstimate?.maxCreditsPerUpdate ?? 0}
      available={0}
      allowOverage={false}
      isLoading={false}
      insufficientTitle={t('socialListening.credits.confirm.insufficient')}
      showShortfall={false}
      action={<SocialCreditsCta isOrgOwner={isOrgOwner} testId="social-universe-estimate" />}
    />
  ) : null

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!isSaving) {
          onOpenChange(next)
        }
      }}
    >
      <DialogContent
        hideClose={isSaving}
        aria-describedby={undefined}
        className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] max-w-[720px] flex-col gap-0 overflow-hidden rounded-2xl p-0 sm:h-[min(760px,calc(100dvh-2rem))] sm:max-w-[720px] sm:p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          titleRef.current?.focus()
        }}
        onInteractOutside={(event) => event.preventDefault()}
        data-testid="social-universe-dialog"
      >
        <div className="flex shrink-0 flex-col gap-4 border-b border-border px-5 pb-4 pt-6 sm:px-8 sm:pt-8">
          <DialogTitle ref={titleRef} tabIndex={-1} className="pr-8 outline-none">
            {t(
              universe
                ? 'socialListening.topicDialog.editTitle'
                : 'socialListening.topicDialog.newTitle',
            )}
          </DialogTitle>
          <SocialUniverseStepper
            current={step}
            isReachable={isReachable}
            isComplete={isComplete}
            onSelect={goTo}
          />
        </div>
        <form
          className="flex min-h-0 flex-1 flex-col"
          data-testid="social-universe-form"
          data-step={step}
          noValidate
          aria-busy={isSaving}
          onSubmit={(event) => {
            event.preventDefault()
            if (isEditing || isLastStep) {
              if (isCreateBlocked) {
                return
              }
              void onSave()
              return
            }
            onNext()
          }}
        >
          <div
            ref={bodyRef}
            className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto overscroll-contain px-5 py-6 sm:px-8"
          >
            <div className="flex flex-col gap-1">
              <Heading
                ref={stepHeadingRef}
                level={3}
                size="lg"
                weight="medium"
                tabIndex={-1}
                className="outline-none"
                data-testid="social-universe-step-heading"
              >
                {t(stepTitleKey)}
              </Heading>
              <Text size="sm" color="muted">
                {t(`socialListening.topicDialog.stepHints.${step}`)}
              </Text>
            </div>

            <fieldset disabled={isSaving} className="flex min-w-0 flex-col gap-5">
              {step === 'topic' ? (
                <UniverseTopicStep
                  wizard={wizard}
                  fieldError={fieldError}
                  languageLabel={languageLabel}
                />
              ) : null}
              {step === 'brands' ? (
                <UniverseBrandsStep
                  wizard={wizard}
                  fieldError={fieldError}
                  hasLanded={suggestLanding === 'landed'}
                />
              ) : null}
              {step === 'channels' ? (
                <UniverseChannelsStep wizard={wizard} fieldError={fieldError} />
              ) : null}
              {step === 'tracking' ? <UniverseTrackingStep wizard={wizard} /> : null}
              {step === 'review' ? (
                <SocialUniverseReview
                  form={form}
                  channels={universe?.channels ?? []}
                  languageLabel={languageLabel}
                  onEdit={goTo}
                />
              ) : null}
            </fieldset>

            {hasEstimate ? (
              <div className="rounded-xl bg-muted/50 p-4">
                <SocialUsageEstimate
                  estimate={serverEstimate}
                  cadence={form.cadence}
                  runPostLimit={form.runPostLimit}
                  firstCollectionPostLimit={form.firstCollectionPostLimit}
                  credits={credits}
                  firstCollectionCredits={firstCollectionCredits}
                  isLoading={creditEstimate.isLoading}
                  isRefreshing={creditEstimate.isRefreshing}
                  hasError={creditEstimate.hasError}
                  payerName={payerName}
                  funds={step === 'review' ? funds : null}
                  expectsFirstCollection={!isEditing}
                />
              </div>
            ) : null}
          </div>

          <div className="flex shrink-0 flex-col gap-3 border-t border-border bg-background px-5 py-4 sm:px-8">
            {isEditing && wizard.changesTags ? (
              <Text size="xs" color="muted" data-testid="social-universe-retag-note">
                {t('socialListening.topicDialog.retagNote')}
              </Text>
            ) : null}
            {wizard.error ? (
              <div
                className="flex flex-col items-start gap-2 rounded-lg border border-destructive/20 bg-destructive/5 p-3"
                role="alert"
                data-testid="social-universe-error"
              >
                <Text size="sm" className="text-destructive">
                  {wizard.error}
                </Text>
                {wizard.isOutOfCredits ? (
                  <SocialCreditsCta isOrgOwner={isOrgOwner} testId="social-universe-error" />
                ) : null}
              </div>
            ) : null}
            {step === 'topic' && wizard.suggestFailed && !wizard.isSuggesting ? (
              <Text
                as="p"
                size="xs"
                className="text-destructive"
                role="alert"
                data-testid="social-universe-suggest-error"
              >
                {wizard.suggestFailure === 'paused'
                  ? t('socialListening.topicDialog.suggestPaused')
                  : wizard.suggestFailure === 'spend_cap'
                    ? t('socialListening.topicDialog.suggestSpendCap')
                    : t('socialListening.topicDialog.suggestFailed')}
              </Text>
            ) : null}
            {step === 'topic' &&
            suggestLanding === 'ready' &&
            !wizard.isSuggesting &&
            !wizard.suggestFailed ? (
              <Text
                as="p"
                size="xs"
                color="muted"
                role="status"
                data-testid="social-universe-suggest-ready"
              >
                {t('socialListening.topicDialog.suggestReady')}
              </Text>
            ) : null}
            <div className="flex items-center justify-between gap-3">
              {stepIndex > 0 ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="gap-1.5"
                  disabled={isSaving}
                  data-testid="social-universe-back"
                  onClick={onBack}
                >
                  <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                  {t('socialListening.topicDialog.back')}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isSaving}
                  data-testid="social-universe-cancel"
                  onClick={() => onOpenChange(false)}
                >
                  {t('socialListening.form.cancel')}
                </Button>
              )}
              <div className="flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-2">
                {/* Why Next (or, on Topic, the pressed button) waits sits beside it, in the button row. */}
                {nextReason ? (
                  <Text
                    as="p"
                    size="xs"
                    color="muted"
                    className="min-w-0 text-right"
                    role={step === 'topic' ? 'status' : undefined}
                    id="social-universe-next-reason"
                    data-testid="social-universe-next-reason"
                  >
                    {nextReason}
                  </Text>
                ) : null}
                {/* Decorative (aria-hidden): the button's "Suggesting…" label carries the state. */}
                {step === 'topic' && wizard.isSuggesting ? (
                  <Text
                    as="span"
                    size="xs"
                    color="muted"
                    className="min-w-0 text-right"
                    aria-hidden="true"
                    data-testid="social-universe-suggest-loading"
                  >
                    {t(suggestLoadingKey)}
                  </Text>
                ) : null}
                {isTopicStart && !wizard.isSuggesting ? (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSaving}
                    aria-describedby={
                      blockedPress === 'manual' && nextReason
                        ? 'social-universe-next-reason'
                        : undefined
                    }
                    data-testid="social-universe-setup-manually"
                    onClick={onSetUpManually}
                  >
                    {t('socialListening.topicDialog.setUpManually')}
                  </Button>
                ) : null}
                {step === 'topic' ? (
                  <Button
                    type="button"
                    variant={isEditing ? 'outline' : 'cta'}
                    className="gap-1.5"
                    disabled={isSaving || wizard.isSuggesting}
                    aria-busy={wizard.isSuggesting || undefined}
                    aria-describedby={
                      blockedPress === 'suggest' && nextReason
                        ? 'social-universe-next-reason'
                        : undefined
                    }
                    data-testid="social-universe-suggest-primary"
                    onClick={onSuggest}
                  >
                    {wizard.isSuggesting ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : (
                      <Wand2 className="h-4 w-4" aria-hidden="true" />
                    )}
                    {t(
                      wizard.isSuggesting
                        ? 'socialListening.topicDialog.suggesting'
                        : 'socialListening.topicDialog.suggest',
                    )}
                  </Button>
                ) : null}
                {!isLastStep && !isTopicStart ? (
                  <Button
                    type="button"
                    variant={isEditing ? 'outline' : 'cta'}
                    className="gap-1.5"
                    disabled={isSaving || stepReason !== null}
                    aria-describedby={stepReason ? 'social-universe-next-reason' : undefined}
                    data-testid="social-universe-next"
                    onClick={onNext}
                  >
                    {t('socialListening.topicDialog.next')}
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Button>
                ) : null}
                {isEditing || isLastStep ? (
                  <Button
                    type="submit"
                    variant="cta"
                    disabled={isSaving || isCreateBlocked}
                    aria-describedby={isCreateBlocked ? 'social-universe-next-reason' : undefined}
                    data-testid="social-universe-submit"
                  >
                    {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                    {saveLabel}
                  </Button>
                ) : null}
              </div>
            </div>
          </div>
        </form>

        <AlertDialog
          open={wizard.pendingRemoval !== null}
          onOpenChange={(next) => {
            if (!next) {
              wizard.cancelRemoval()
            }
          }}
        >
          <AlertDialogContent data-testid="social-universe-remove-channel">
            <AlertDialogHeader>
              <AlertDialogTitle>
                {t('socialListening.topicDialog.removeChannelTitle', {
                  platform: removalLabel,
                })}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {t('socialListening.topicDialog.removeChannelBody')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="social-universe-remove-channel-cancel">
                {t('socialListening.form.cancel')}
              </AlertDialogCancel>
              <AlertDialogAction
                data-testid="social-universe-remove-channel-confirm"
                onClick={wizard.confirmRemoval}
              >
                {t('socialListening.topicDialog.removeChannelConfirm')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  )
}

interface UniverseStepProps {
  wizard: SocialUniverseWizard
}

interface UniverseFieldStepProps extends UniverseStepProps {
  fieldError: FieldError
}

const SUGGEST_LOADING_KEYS = [
  'socialListening.topicDialog.suggestLoading.goal',
  'socialListening.topicDialog.suggestLoading.brands',
  'socialListening.topicDialog.suggestLoading.searches',
] as const

/** How long each Suggest loading line stays before the next. */
const SUGGEST_LOADING_INTERVAL_MS = 1800

/**
 * The line beside a running Suggest: what it is doing, in turn, holding on the
 * last line (going back to the first reads as a restart).
 */
function useSuggestLoadingKey(isSuggesting: boolean) {
  const [loadingIndex, setLoadingIndex] = useState(0)

  useEffect(() => {
    if (!isSuggesting) {
      setLoadingIndex(0)
      return
    }
    if (loadingIndex >= SUGGEST_LOADING_KEYS.length - 1) {
      return
    }
    const timer = setTimeout(
      () => setLoadingIndex((current) => Math.min(current + 1, SUGGEST_LOADING_KEYS.length - 1)),
      SUGGEST_LOADING_INTERVAL_MS,
    )
    return () => clearTimeout(timer)
  }, [isSuggesting, loadingIndex])

  return SUGGEST_LOADING_KEYS[loadingIndex]
}

interface SuggestionChipsProps {
  testId: string
  values: Array<string>
  ariaLabel: (value: string) => string
  onAdd: (value: string) => void
}

/** What Suggest offered and the form does not hold yet; one click adds one. */
function SuggestionChips(props: SuggestionChipsProps) {
  const { testId, values, ariaLabel, onAdd } = props

  const { t } = useTranslation('app')

  if (!values.length) {
    return null
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5 pt-1" data-testid={testId}>
      <Text as="span" size="xs" color="muted">
        {t('socialListening.topicDialog.keywordSuggestions')}
      </Text>
      {values.map((value, index) => (
        <Button
          key={value}
          type="button"
          variant="outline"
          size="sm"
          shape="pill"
          className="h-7 gap-1 px-2.5 text-[12px]"
          aria-label={ariaLabel(value)}
          data-testid={`${testId}-${index}`}
          onClick={() => onAdd(value)}
        >
          <Plus className="h-3 w-3" aria-hidden="true" />
          {value}
        </Button>
      ))}
    </div>
  )
}

interface UniverseSelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Extra classes on the wrapper (its width). */
  wrapperClassName?: string
}

/** The dialog's one select look: native, the field's own height, a chevron at the end. */
function UniverseSelect(props: UniverseSelectProps) {
  const { wrapperClassName, className, children, ...rest } = props

  return (
    <div className={cn('relative', wrapperClassName)}>
      <select className={cn(inputVariants(), 'appearance-none pr-10', className)} {...rest}>
        {children}
      </select>
      <ChevronDown
        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden="true"
      />
    </div>
  )
}

interface UniverseMarketFieldsProps extends UniverseFieldStepProps {
  languageLabel: (code: string) => string
}

/** The market and languages that frame the Topic: Suggest writes its searches and communities for them. */
function UniverseMarketFields(props: UniverseMarketFieldsProps) {
  const { wizard, fieldError, languageLabel } = props

  const { t, i18n } = useTranslation('app')
  const { form, missing } = wizard

  return (
    <div className="grid grid-cols-1 items-start gap-4 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor="social-universe-market">{t('socialListening.topicDialog.market')}</Label>
        <UniverseSelect
          id="social-universe-market"
          value={socialMarketSelectValue(form)}
          aria-invalid={missing.includes('market') || undefined}
          aria-describedby={missing.includes('market') ? 'social-universe-market-error' : undefined}
          data-testid="social-universe-market-input"
          onChange={(event) => {
            wizard.updateMarket(socialMarketFromSelect(event.target.value, form))
          }}
        >
          <option value="" disabled>
            {t('socialListening.form.selectMarket')}
          </option>
          {socialMarketSelectValue(form) === SOCIAL_UNLISTED_MARKET ? (
            <option value={SOCIAL_UNLISTED_MARKET}>
              {localizedCountryName(form.market, i18n.language)}
            </option>
          ) : null}
          {COUNTRIES.map((country) => (
            <option key={country.code} value={country.code}>
              {localizedCountryName(country.code, i18n.language)}
            </option>
          ))}
        </UniverseSelect>
        {fieldError('market')}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="social-tracker-language">
          {t('socialListening.topicDialog.languages')}
        </Label>
        {/* The picker lines up with the market; the picked languages sit under it. */}
        {form.languages.length < SOCIAL_MAX_LANGUAGES ? (
          <SocialTrackerLanguageField
            value=""
            invalid={missing.includes('languages')}
            onChange={wizard.addLanguage}
          />
        ) : null}
        {form.languages.length ? (
          <ul className="flex flex-wrap gap-1.5" data-testid="social-universe-languages">
            {form.languages.map((code) => (
              <li
                key={code}
                className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-sm text-foreground"
              >
                <span>{languageLabel(code)}</span>
                <button
                  type="button"
                  className="shrink-0 rounded-sm p-0.5 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  aria-label={t('socialListening.topicDialog.removeLanguageAria', {
                    language: languageLabel(code),
                  })}
                  data-testid={`social-universe-language-remove-${code}`}
                  onClick={() => wizard.removeLanguage(code)}
                >
                  <X className="h-3 w-3" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {form.languages.length >= SOCIAL_MAX_LANGUAGES ? (
          <Text
            as="span"
            size="xs"
            color="muted"
            className="block"
            data-testid="social-universe-languages-limit"
          >
            {t('socialListening.topicDialog.languagesLimit', { max: SOCIAL_MAX_LANGUAGES })}
          </Text>
        ) : null}
        {fieldError('languages')}
      </div>
    </div>
  )
}


/**
 * Step 1 (D21b): the name, what the client wants to learn, the market and
 * languages, which Suggest (the footer's main button) reads. What it fills is on step 2.
 */
function UniverseTopicStep(props: UniverseMarketFieldsProps) {
  const { wizard, fieldError, languageLabel } = props

  const { t } = useTranslation('app')
  const { form, missing } = wizard

  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="social-universe-name">{t('socialListening.topicDialog.name')}</Label>
        <Input
          id="social-universe-name"
          maxLength={255}
          value={form.name}
          placeholder={t('socialListening.topicDialog.namePlaceholder')}
          aria-invalid={missing.includes('name') || undefined}
          aria-describedby={missing.includes('name') ? 'social-universe-name-error' : undefined}
          data-testid="social-universe-name-input"
          onChange={(event) => wizard.updateField('name', event.target.value)}
        />
        {fieldError('name')}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="social-universe-goal">{t('socialListening.topicDialog.about')}</Label>
        <Input
          id="social-universe-goal"
          maxLength={SOCIAL_BRIEF_MAX_LENGTH}
          value={form.goal}
          placeholder={t('socialListening.topicDialog.aboutPlaceholder')}
          aria-describedby="social-universe-goal-hint"
          data-testid="social-universe-goal-input"
          onChange={(event) => wizard.updateField('goal', event.target.value)}
        />
        <Text as="span" size="xs" color="muted" className="block" id="social-universe-goal-hint">
          {t('socialListening.topicDialog.goalHint')}
        </Text>
      </div>

      <UniverseMarketFields wizard={wizard} fieldError={fieldError} languageLabel={languageLabel} />
    </>
  )
}

interface UniverseBrandsStepProps extends UniverseFieldStepProps {
  /** Suggest just brought the client here: one line says so. */
  hasLanded: boolean
}

/**
 * Step 2 (D21b): what Suggest fills, all editable: the subject, what the
 * Topic is about, the brands and their cards, and what posts must be about.
 */
function UniverseBrandsStep(props: UniverseBrandsStepProps) {
  const { wizard, fieldError, hasLanded } = props

  const { t } = useTranslation('app')
  const { form, drafts, missing } = wizard

  // Typed but not yet entered keywords count toward the limit too.
  const keywordsOver = keywordsOverLimit(flushUniverseDrafts(form, drafts))

  const brandNames = form.brands.map((brand) => brand.name)
  const [isMoreOpen, setIsMoreOpen] = useState(false)

  return (
    <>
      {hasLanded ? (
        <div
          className="flex items-center gap-2 rounded-lg bg-primary/5 px-3 py-2"
          role="status"
          data-testid="social-universe-suggest-landed"
        >
          <Wand2 className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
          <Text as="span" size="sm">
            {t('socialListening.topicDialog.suggestLanded')}
          </Text>
        </div>
      ) : null}

      <div className="space-y-1.5">
        <Label htmlFor="social-universe-keywords">
          {t('socialListening.topicDialog.keywords')}
        </Label>
        <SocialTermsInput
          id="social-universe-keywords"
          values={form.keywords}
          draft={drafts.keywords}
          placeholder={t('socialListening.topicDialog.keywordsPlaceholder')}
          testId="social-universe-keywords-input"
          isInvalid={missing.includes('keywords') || keywordsOver > 0}
          describedBy={
            keywordsOver > 0
              ? 'social-universe-keywords-hint social-universe-keywordLimit-error'
              : 'social-universe-keywords-hint'
          }
          onDraftChange={(value) => wizard.updateDraft('keywords', value)}
          onChange={(values) => wizard.updateField('keywords', values)}
        />
        <Text as="span" size="xs" color="muted" className="block" id="social-universe-keywords-hint">
          {t('socialListening.topicDialog.keywordsHint', { max: SOCIAL_MAX_KEYWORDS })}
        </Text>
        {fieldError('keywords')}
        {keywordsOver > 0 ? (
          <Text
            as="span"
            size="xs"
            className="block text-destructive"
            role="alert"
            id="social-universe-keywordLimit-error"
            data-testid="social-universe-keywordLimit-error"
          >
            {t('socialListening.topicDialog.keywordLimit', {
              max: SOCIAL_MAX_KEYWORDS,
              count: keywordsOver,
            })}
          </Text>
        ) : null}
        <SuggestionChips
          testId="social-universe-keyword-suggestion"
          values={wizard.suggestedKeywords}
          ariaLabel={(keyword) => t('socialListening.topicDialog.addKeywordAria', { keyword })}
          onAdd={wizard.addSuggestedKeyword}
        />
        {wizard.keywordWarnings.length ? (
          <Text
            as="span"
            size="xs"
            color="muted"
            className="block"
            data-testid="social-universe-keyword-warning"
          >
            {t('socialListening.topicDialog.keywordWarning', {
              count: wizard.keywordWarnings.length,
              keywords: wizard.keywordWarnings.map((warning) => warning.keyword).join(', '),
            })}
          </Text>
        ) : null}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="social-universe-subject-kind">
          {t('socialListening.topicDialog.subjectKind')}
        </Label>
        <UniverseSelect
          id="social-universe-subject-kind"
          value={form.subjectKind}
          wrapperClassName="max-w-[16rem]"
          aria-describedby="social-universe-subject-kind-hint"
          data-testid="social-universe-subject-kind"
          onChange={(event) => wizard.pickSubjectKind(event.target.value as SocialSubjectKind)}
        >
          {SOCIAL_SUBJECT_KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {t(`socialListening.topicDialog.subjectKinds.${kind}`)}
            </option>
          ))}
        </UniverseSelect>
        <Text as="span" size="xs" color="muted" className="block" id="social-universe-subject-kind-hint">
          {t('socialListening.topicDialog.subjectKindHint')}
        </Text>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="social-universe-brands">{t('socialListening.topicDialog.brands')}</Label>
        <SocialTermsInput
          id="social-universe-brands"
          values={brandNames}
          draft={drafts.brands}
          placeholder={t('socialListening.topicDialog.brandsPlaceholder')}
          testId="social-universe-brands-input"
          describedBy="social-universe-brands-hint"
          onDraftChange={(value) => wizard.updateDraft('brands', value)}
          onChange={(values) => wizard.updateField('brands', withBrandNames(form.brands, values))}
        />
        <Text as="span" size="xs" color="muted" className="block" id="social-universe-brands-hint">
          {form.brands.length >= SOCIAL_MAX_BRANDS
            ? t('socialListening.topicDialog.brandsLimit', { max: SOCIAL_MAX_BRANDS })
            : t('socialListening.topicDialog.brandsHint')}
        </Text>
        <SuggestionChips
          testId="social-universe-brand-suggestion"
          values={wizard.suggestedBrands.map((brand) => brand.name)}
          ariaLabel={(name) => t('socialListening.topicDialog.addBrandAria', { name })}
          onAdd={(name) => {
            const brand = wizard.suggestedBrands.find((entry) => entry.name === name)
            if (brand) {
              wizard.addSuggestedBrand(brand)
            }
          }}
        />
        <RefusedBrandLines wizard={wizard} />
      </div>

      {form.brands.length ? (
        <div className="flex flex-col gap-3" data-testid="social-universe-brand-rows">
          {form.brands.map((brand, index) => (
            <BrandRow
              key={brand.id ?? `new-${brand.name}`}
              wizard={wizard}
              brand={brand}
              index={index}
            />
          ))}
        </div>
      ) : null}

      <details
        className="border-t border-border pt-4"
        open={isMoreOpen}
        onToggle={(event) => setIsMoreOpen((event.currentTarget as HTMLDetailsElement).open)}
        data-testid="social-universe-more-options"
      >
        <summary className="cursor-pointer text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          {t('socialListening.topicDialog.moreOptions')}
        </summary>
        <div className="flex flex-col gap-4 pt-4">
          <div className="space-y-1.5">
            <Label htmlFor="social-universe-include">
              {t('socialListening.form.briefInclude')}
            </Label>
            <Input
              id="social-universe-include"
              maxLength={SOCIAL_BRIEF_MAX_LENGTH}
              value={form.brief.include}
              placeholder={t('socialListening.form.briefIncludePlaceholder')}
              data-testid="social-universe-include-input"
              onChange={(event) =>
                wizard.updateField('brief', { ...form.brief, include: event.target.value })
              }
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="social-universe-exclude">
              {t('socialListening.form.briefExclude')}
            </Label>
            <Input
              id="social-universe-exclude"
              maxLength={SOCIAL_BRIEF_MAX_LENGTH}
              value={form.brief.exclude}
              placeholder={t('socialListening.form.briefExcludePlaceholder')}
              data-testid="social-universe-exclude-input"
              onChange={(event) =>
                wizard.updateField('brief', { ...form.brief, exclude: event.target.value })
              }
            />
          </div>
        </div>
      </details>
    </>
  )
}

interface BrandRowProps {
  wizard: SocialUniverseWizard
  brand: SocialBrandInput
  index: number
}

/**
 * One brand card on step 2 (D20): a header with its name, role and whether it
 * is searched; its searches (at most SOCIAL_BRAND_SEARCHES_MAX, the same kinds
 * for every brand); its hashtags on channels that follow them; then, quieter,
 * its other names and its accounts.
 */
function BrandRow(props: BrandRowProps) {
  const { wizard, brand, index } = props

  const { t } = useTranslation('app')
  const { form } = wizard
  const [searchDraft, setSearchDraft] = useState('')
  const [hashtagDraft, setHashtagDraft] = useState('')

  const setBrand = (next: SocialBrandInput) =>
    wizard.updateField(
      'brands',
      form.brands.map((entry, i) => (i === index ? next : entry)),
    )
  const followsHashtags = form.platforms.some(
    (platform) => SOCIAL_PLATFORM_SOURCE_KINDS[platform].hashtag,
  )
  const searches = (brand.searches ?? []).map((search) => search.query)
  const hashtags = (brand.hashtags ?? []).map((hashtag) => `#${hashtag.tag}`)

  return (
    <div
      className="flex flex-col gap-3 rounded-xl border border-border p-4"
      data-testid={`social-universe-brand-row-${index}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Text as="span" size="sm" className="min-w-0 flex-1 truncate font-medium">
          {brand.name}
        </Text>
        <UniverseSelect
          value={brand.role ?? ''}
          wrapperClassName="w-40 shrink-0"
          aria-label={t('socialListening.topicDialog.brandRoleAria', { name: brand.name })}
          data-testid={`social-universe-brand-role-${index}`}
          onChange={(event) => setBrand({ ...brand, role: event.target.value as SocialBrandRole })}
        >
          <option value="" disabled>
            {t('socialListening.topicDialog.brandRole')}
          </option>
          {SOCIAL_BRAND_ROLES.map((role) => (
            <option key={role} value={role}>
              {t(`socialListening.topicDialog.brandRoles.${role}`)}
            </option>
          ))}
        </UniverseSelect>
        <label className="inline-flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
          {t('socialListening.topicDialog.brandCollect')}
          <Switch
            checked={brand.collect}
            aria-label={t('socialListening.topicDialog.brandCollectAria', { name: brand.name })}
            data-testid={`social-universe-brand-collect-${index}`}
            onCheckedChange={(collect) => setBrand({ ...brand, collect })}
          />
        </label>
      </div>
      <div className="space-y-1.5">
        <Label
          htmlFor={`social-universe-brand-searches-${index}`}
          className="text-xs font-normal text-muted-foreground"
        >
          {t('socialListening.topicDialog.brandSearches', { max: SOCIAL_BRAND_SEARCHES_MAX })}
        </Label>
        <SocialTermsInput
          id={`social-universe-brand-searches-${index}`}
          values={searches}
          draft={searchDraft}
          placeholder={t('socialListening.topicDialog.searchesPlaceholder')}
          testId={`social-universe-brand-searches-${index}`}
          isInvalid={searches.length === 0 || undefined}
          onDraftChange={setSearchDraft}
          onChange={(values) => setBrand(withBrandSearchValues(brand, values))}
        />
      </div>
      {followsHashtags ? (
        <div className="space-y-1.5">
          <Label
            htmlFor={`social-universe-brand-hashtags-${index}`}
            className="text-xs font-normal text-muted-foreground"
          >
            {t('socialListening.topicDialog.hashtags')}
          </Label>
          <SocialTermsInput
            id={`social-universe-brand-hashtags-${index}`}
            values={hashtags}
            draft={hashtagDraft}
            placeholder={t('socialListening.topicDialog.hashtagsPlaceholder')}
            testId={`social-universe-brand-hashtags-${index}`}
            onDraftChange={setHashtagDraft}
            onChange={(values) => setBrand(withBrandHashtagValues(brand, values))}
          />
        </div>
      ) : null}
      {/* The quieter part: other names and the brand's own accounts. */}
      <div className="flex flex-col gap-3 rounded-lg bg-muted/40 p-3">
        <div className="space-y-1.5">
          <Label
            htmlFor={`social-universe-brand-aliases-${index}`}
            className="text-xs font-normal text-muted-foreground"
          >
            {t('socialListening.topicDialog.brandAliases')}
          </Label>
          {/* Other names are the brand's aliases; `description` (what it covers) is kept as saved. */}
          <Input
            id={`social-universe-brand-aliases-${index}`}
            maxLength={SOCIAL_NOTE_MAX_LENGTH}
            value={brandAliasesText(brand)}
            aria-label={t('socialListening.topicDialog.brandDescriptionAria', { name: brand.name })}
            placeholder={t('socialListening.topicDialog.brandDescriptionPlaceholder')}
            data-testid={`social-universe-brand-aliases-${index}`}
            onChange={(event) => setBrand({ ...brand, aliases: aliasesFromText(event.target.value) })}
          />
        </div>
        <BrandAccounts wizard={wizard} brand={brand} index={index} />
      </div>
    </div>
  )
}

/** Suggested brands and brand accounts the last Suggest did not keep, each with its typed reason. */
function RefusedBrandLines(props: UniverseStepProps) {
  const { wizard } = props

  const { t } = useTranslation('app')

  const accounts = wizard.refusedSources.filter((source) => source.kind === 'account')
  if (!wizard.refusedBrands.length && !accounts.length) {
    return null
  }
  return (
    <ul className="space-y-0.5" data-testid="social-universe-refused-brands">
      {wizard.refusedBrands.map((refused) => (
        <li key={`brand-${refused.name}`}>
          <Text as="span" size="xs" color="muted">
            {t(`socialListening.topicDialog.refusedBrand.${refused.refusal}`, {
              name: refused.name,
              duplicateOf: refused.duplicateOf,
            })}
          </Text>
        </li>
      ))}
      {accounts.map((source) => (
        <li key={`account-${source.platform}-${source.value}`}>
          <Text as="span" size="xs" color="muted">
            {t('socialListening.topicDialog.refusedSource.account', {
              value: source.value,
              brand: source.brandName ?? '',
              platform: SOCIAL_PLATFORM_LABELS[source.platform],
            })}
          </Text>
        </li>
      ))}
    </ul>
  )
}

interface BrandAccountsProps {
  wizard: SocialUniverseWizard
  brand: SocialBrandInput
  index: number
}

/**
 * A brand card's own account per picked channel that offers accounts, each
 * with its check (a dot and a short label) and a Confirm tick: a proposed
 * account its check verified comes ticked (D20), any other stays unticked
 * until the client ticks it; an account the client types is ticked and checked.
 */
function BrandAccounts(props: BrandAccountsProps) {
  const { wizard, brand, index } = props

  const { t } = useTranslation('app')
  const [drafts, setDrafts] = useState<Partial<Record<SocialPlatform, string>>>({})

  const platforms = SOCIAL_UNIVERSE_PLATFORM_ORDER.filter(
    (platform) =>
      SOCIAL_PLATFORM_SOURCE_KINDS[platform].account &&
      (wizard.form.platforms.includes(platform) || brand.accounts?.[platform]),
  )

  const addTyped = (platform: SocialPlatform) => {
    const handle = (drafts[platform] ?? '').trim()
    if (!handle) {
      return
    }
    setDrafts((current) => ({ ...current, [platform]: '' }))
    wizard.addTypedBrandAccount(index, platform, handle)
  }

  if (!platforms.length) {
    return null
  }

  return (
    <div className="space-y-1.5" data-testid={`social-universe-brand-extras-${index}`}>
      <Text as="span" size="xs" color="muted" className="block">
        {t('socialListening.topicDialog.brandAccounts')}
      </Text>
      <ul className="flex flex-col gap-1.5" aria-label={t('socialListening.topicDialog.brandAccounts')}>
        {platforms.map((platform) => {
          const account = brand.accounts?.[platform]
          const checkLabel = account
            ? sourceCheckLabelKey(
                account.check,
                wizard.sourceCheckState('account', platform, account.handle, brand.name),
              )
            : null
          const label = SOCIAL_PLATFORM_LABELS[platform]
          const testId = `social-universe-brand-account-${index}-${platform}`
          return (
            <li key={platform} className="flex min-w-0 flex-wrap items-center gap-2 text-sm" data-testid={testId}>
              <SocialPlatformIcon platform={platform} className="h-4 w-4 shrink-0" />
              {account ? (
                <>
                  <span className="truncate font-medium text-foreground">@{account.handle}</span>
                  {checkLabel ? (
                    <Text
                      as="span"
                      size="xs"
                      color="muted"
                      className="inline-flex items-center gap-1.5"
                      title={
                        checkLabel === 'socialListening.topicDialog.accountCheck.age_restricted'
                          ? t('socialListening.topicDialog.accountCheckHint.age_restricted', { platform: label })
                          : undefined
                      }
                      data-testid={`${testId}-check`}
                    >
                      <SocialNoteDot tone={sourceCheckTone(checkLabel)} />
                      {t(checkLabel)}
                    </Text>
                  ) : null}
                  {/* A verified account comes ticked (D20); the client unticks it to leave it out. */}
                  <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Checkbox
                      checked={account.confirmed}
                      aria-label={t('socialListening.topicDialog.confirmAccountAria', {
                        handle: account.handle,
                        name: brand.name,
                        platform: label,
                      })}
                      data-testid={`${testId}-confirm`}
                      onCheckedChange={(checked) =>
                        wizard.setBrandAccount(index, platform, { ...account, confirmed: checked === true })
                      }
                    />
                    {t('socialListening.topicDialog.confirmAccount')}
                  </label>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    aria-label={t('socialListening.topicDialog.removeAccountAria', {
                      handle: account.handle,
                    })}
                    data-testid={`${testId}-remove`}
                    onClick={() => wizard.setBrandAccount(index, platform, null)}
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </>
              ) : (
                <Input
                  value={drafts[platform] ?? ''}
                  className="h-8 max-w-[16rem]"
                  maxLength={SOCIAL_SEARCH_TERM_MAX_LENGTH}
                  placeholder={t('socialListening.topicDialog.brandAccountPlaceholder', {
                    platform: label,
                  })}
                  aria-label={t('socialListening.topicDialog.brandAccountAria', {
                    platform: label,
                    name: brand.name,
                  })}
                  data-testid={`${testId}-input`}
                  onChange={(event) =>
                    setDrafts((current) => ({ ...current, [platform]: event.target.value }))
                  }
                  onBlur={() => addTyped(platform)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault()
                      addTyped(platform)
                    }
                  }}
                />
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** Step 3: the channel picker, and what each picked channel searches for (D21b). */
function UniverseChannelsStep(props: UniverseFieldStepProps) {
  const { wizard, fieldError } = props

  const { t } = useTranslation('app')
  const { form, drafts, missing } = wizard

  return (
    <>
      <fieldset className="flex min-w-0 flex-col gap-2" data-testid="social-universe-channels">
        <legend className="mb-1.5 text-sm font-medium">{t('socialListening.topicDialog.channels')}</legend>
        <SocialUniverseChannelCards
          form={form}
          drafts={drafts}
          refusedSources={wizard.refusedSources}
          checkState={(kind, platform, value) => wizard.sourceCheckState(kind, platform, value, null)}
          isInvalid={missing.includes('channels')}
          isSearchesInvalid={missing.includes('searches')}
          onToggle={wizard.togglePlatform}
          onSearchDraftChange={wizard.updateSearchDraft}
          onSearchesChange={wizard.updateSearches}
          onHashtagDraftChange={wizard.updateHashtagDraft}
          onHashtagsChange={wizard.updateHashtags}
          onCommunityDraftChange={wizard.updateCommunityDraft}
          onCommunitiesChange={wizard.updateCommunities}
          onHandleDraftChange={wizard.updateHandleDraft}
          onHandlesChange={wizard.updateHandles}
        />
        {fieldError('channels')}
        {fieldError('searches')}
      </fieldset>
    </>
  )
}

/** Step 4: how much we collect, how often, and the themes posts are sorted into. */
function UniverseTrackingStep(props: UniverseStepProps) {
  const { wizard } = props

  const { t } = useTranslation('app')
  const { form } = wizard

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <SocialTrackerScheduleFields
          depthDays={form.depthDays}
          cadence={form.cadence}
          runPostLimit={form.runPostLimit}
          isEditing={false}
          onDepthDaysChange={(value) => wizard.updateField('depthDays', value)}
          onCadenceChange={(value) => wizard.updateField('cadence', value)}
          onRunPostLimitChange={(value) => wizard.updateField('runPostLimit', value)}
          firstCollectionPostLimit={form.firstCollectionPostLimit}
          onFirstCollectionPostLimitChange={(value) =>
            wizard.updateField('firstCollectionPostLimit', value)
          }
        />
        {wizard.isEditing ? (
          <Text
            as="span"
            size="xs"
            color="muted"
            className="block"
            data-testid="social-universe-depth-edit-hint"
          >
            {t('socialListening.topicDialog.depthEditHint')}
          </Text>
        ) : null}
      </div>

      <SocialUniverseThemes
        wizard={wizard}
        topics={form.topics}
        onTopicsChange={(value) => wizard.updateField('topics', value)}
      />
    </>
  )
}

interface SocialUniverseThemesProps {
  wizard: SocialUniverseWizard
  topics: Array<SocialTopicInput>
  onTopicsChange: (topics: Array<SocialTopicInput>) => void
}

/** Themes as editable rows: rename, describe, remove, add up to the limit. */
function SocialUniverseThemes(props: SocialUniverseThemesProps) {
  const { wizard, topics, onTopicsChange } = props

  const { t } = useTranslation('app')
  const lastNameRef = useRef<HTMLInputElement>(null)
  const [hasAdded, setHasAdded] = useState(false)

  useEffect(() => {
    if (hasAdded) {
      lastNameRef.current?.focus()
      setHasAdded(false)
    }
  }, [hasAdded])

  const onUpdate = (index: number, patch: Partial<SocialTopicInput>) => {
    onTopicsChange(topics.map((topic, i) => (i === index ? { ...topic, ...patch } : topic)))
  }

  const onAdd = () => {
    onTopicsChange([...topics, { name: '', description: '' }])
    setHasAdded(true)
  }

  const named = topics.filter((topic) => topic.name.trim() !== '')
  const title = named.length
    ? t('socialListening.topicDialog.themesCount', { count: named.length })
    : t('socialListening.topicDialog.themes')

  return (
    <section
      className="flex flex-col gap-2 border-t border-border pt-5"
      aria-labelledby="social-universe-themes-title"
      data-testid="social-universe-themes"
    >
      <div className="flex items-center justify-between gap-3">
        <Text as="span" size="sm" className="font-medium" id="social-universe-themes-title">
          {title}
        </Text>
      </div>
      <Text as="span" size="xs" color="muted" className="block">
        {t('socialListening.topicDialog.themesHint')}
      </Text>
      <SuggestionChips
        testId="social-universe-theme-suggestion"
        values={wizard.suggestedThemes.map((theme) => theme.name)}
        ariaLabel={(name) => t('socialListening.topicDialog.addThemeAria', { name })}
        onAdd={(name) => {
          const theme = wizard.suggestedThemes.find((entry) => entry.name === name)
          if (theme) {
            wizard.addSuggestedTheme(theme)
          }
        }}
      />
      {topics.length === 0 ? (
        <Text as="span" size="sm" color="muted" data-testid="social-universe-themes-empty">
          {t('socialListening.topicDialog.themesEmpty')}
        </Text>
      ) : null}
      <div className="flex flex-col gap-2 pt-1" role="group" aria-labelledby="social-universe-themes-title">
        {topics.map((topic, index) => (
          <div
            key={topic.id ?? `new-${index}`}
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-2 sm:grid-cols-[minmax(0,14rem)_minmax(0,1fr)_auto]"
            data-testid={`social-universe-theme-${index}`}
          >
            <Input
              ref={index === topics.length - 1 ? lastNameRef : undefined}
              aria-label={t('socialListening.topicDialog.themeName')}
              maxLength={SOCIAL_TOPIC_NAME_MAX_LENGTH}
              value={topic.name}
              placeholder={t('socialListening.topicDialog.themeName')}
              className="col-start-1 row-start-1"
              data-testid={`social-universe-theme-name-${index}`}
              onChange={(event) => onUpdate(index, { name: event.target.value })}
            />
            <Input
              aria-label={t('socialListening.topicDialog.themeDescription')}
              maxLength={SOCIAL_NOTE_MAX_LENGTH}
              value={topic.description}
              placeholder={t('socialListening.topicDialog.themeDescription')}
              className="col-span-2 col-start-1 row-start-2 sm:col-span-1 sm:col-start-2 sm:row-start-1"
              data-testid={`social-universe-theme-description-${index}`}
              onChange={(event) => onUpdate(index, { description: event.target.value })}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="col-start-2 row-start-1 sm:col-start-3"
              aria-label={t('socialListening.topicDialog.removeThemeAria', { name: topic.name })}
              data-testid={`social-universe-theme-remove-${index}`}
              onClick={() => onTopicsChange(topics.filter((_, i) => i !== index))}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
        {topics.length < SOCIAL_MAX_TOPICS ? (
          <Button
            type="button"
            variant="outline"
            shape="pill"
            size="sm"
            className="self-start"
            data-testid="social-universe-theme-add"
            onClick={onAdd}
          >
            <Plus className="h-4 w-4" />
            {t('socialListening.topicDialog.addTheme')}
          </Button>
        ) : (
          <Text as="span" size="xs" color="muted" data-testid="social-universe-themes-limit">
            {t('socialListening.topicDialog.themesLimit', { max: SOCIAL_MAX_TOPICS })}
          </Text>
        )}
      </div>
    </section>
  )
}
